import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Printer, Home, FileCheck2, Cog, CheckCircle2, XCircle, Video, Loader2 } from "lucide-react";
import Header from "../components/Header";
import { getEvidence } from "@/api/evidences";
import { toast } from "sonner";

export default function Evidence() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [insp, setInsp] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await getEvidence(id);
        if (!cancelled) setInsp(data);
      } catch (err) {
        console.error("evidence load", err);
        if (!cancelled) toast.error("No se pudo cargar la evidencia");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [id]);

  if (loading) {
    return (
      <div className="flex-1 flex flex-col bg-white dark:bg-zinc-950">
        <Header title="Evidencia" />
        <div className="flex-1 flex items-center justify-center">
          <Loader2 className="w-6 h-6 text-orange-500 animate-spin" />
        </div>
      </div>
    );
  }

  if (!insp) {
    return (
      <div className="flex-1 flex flex-col bg-white dark:bg-zinc-950">
        <Header title="Evidencia" />
        <div className="p-6 text-zinc-600 dark:text-zinc-400 font-mono-tech text-sm">
          Inspección no encontrada.
        </div>
      </div>
    );
  }

  const formatValue = (field, val) => {
    if (val === undefined || val === null || val === "") return "—";
    if (field.type === "boolean") return val ? "OK" : "KO";
    if (field.unit) return `${val} ${field.unit}`;
    return String(val);
  };

  const sections = Array.isArray(insp.sectionsSnapshot) ? insp.sectionsSnapshot : [];
  const media = Array.isArray(insp.media) ? insp.media : [];

  return (
    <div className="flex-1 flex flex-col overflow-hidden bg-white dark:bg-zinc-950" data-testid="evidence-page">
      <Header title="Evidencia" subtitle={`// ${insp.formTypeCode}`} />

      <div className="flex-1 overflow-y-auto no-scrollbar px-5 pt-5 pb-32">
        <div className="bg-green-500/10 border border-green-500/40 rounded-md p-4 flex items-center gap-3 mb-5">
          <FileCheck2 className="w-6 h-6 text-green-500 shrink-0" />
          <div>
            <p className="font-display font-bold uppercase text-sm text-green-700 dark:text-green-400 tracking-wider">
              Documento listo para garantía
            </p>
            <p className="font-mono-tech text-[10px] text-zinc-600 dark:text-zinc-400 mt-0.5">
              ID: {insp.uid}
            </p>
          </div>
        </div>

        <div
          id="print-area"
          className="bg-zinc-50 dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-md p-5"
          data-testid="print-area"
        >
          <div className="flex items-start justify-between border-b border-dashed border-zinc-300 dark:border-zinc-700 pb-3 mb-4">
            <div>
              <p className="font-mono-tech text-[10px] uppercase tracking-widest text-zinc-500">
                Documento de Garantía
              </p>
              <h2 className="font-display font-bold text-xl uppercase tracking-wider text-zinc-900 dark:text-white">
                Acta de Inspección
              </h2>
            </div>
            <Cog className="w-7 h-7 text-orange-500" />
          </div>

          <dl className="grid grid-cols-2 gap-y-3 gap-x-4 mb-5">
            <div>
              <dt className="font-mono-tech text-[9px] uppercase tracking-widest text-zinc-500">Pieza</dt>
              <dd className="font-display font-bold text-sm text-zinc-900 dark:text-white uppercase">
                {insp.formTypeName}
              </dd>
            </div>
            <div>
              <dt className="font-mono-tech text-[9px] uppercase tracking-widest text-zinc-500">Código</dt>
              <dd className="font-mono-tech text-sm text-zinc-900 dark:text-white">{insp.formTypeCode}</dd>
            </div>
            <div>
              <dt className="font-mono-tech text-[9px] uppercase tracking-widest text-zinc-500">N.º Serie</dt>
              <dd className="font-mono-tech text-sm text-orange-500 font-bold">{insp.serialNumber}</dd>
            </div>
            <div>
              <dt className="font-mono-tech text-[9px] uppercase tracking-widest text-zinc-500">Fecha</dt>
              <dd className="font-mono-tech text-sm text-zinc-900 dark:text-white">
                {new Date(insp.createdAt).toLocaleString()}
              </dd>
            </div>
            <div className="col-span-2">
              <dt className="font-mono-tech text-[9px] uppercase tracking-widest text-zinc-500">
                Ingeniero responsable
              </dt>
              <dd className="font-mono-tech text-sm text-zinc-900 dark:text-white">
                {insp.engineerName || insp.engineerUserUid}
              </dd>
            </div>
          </dl>

          {sections.map((section, idx) => (
            <div key={idx} className="mb-4">
              <h4 className="font-display font-bold text-xs uppercase tracking-widest text-orange-500 border-b border-zinc-200 dark:border-zinc-800 pb-1 mb-2">
                {String(idx + 1).padStart(2, "0")} · {section.title}
              </h4>
              <ul className="space-y-1.5">
                {(section.fields || []).map((field) => {
                  const val = insp.values?.[field.id];
                  const ok =
                    field.type === "boolean" ? val === true : val !== undefined && val !== "";
                  return (
                    <li
                      key={field.id}
                      className="flex items-start justify-between gap-3 text-xs"
                    >
                      <div className="flex items-start gap-1.5 flex-1 min-w-0">
                        {field.type === "boolean" ? (
                          val === true ? (
                            <CheckCircle2 className="w-3.5 h-3.5 text-green-500 mt-0.5 shrink-0" />
                          ) : val === false ? (
                            <XCircle className="w-3.5 h-3.5 text-red-500 mt-0.5 shrink-0" />
                          ) : (
                            <Cog className="w-3.5 h-3.5 text-zinc-400 dark:text-zinc-600 mt-0.5 shrink-0" />
                          )
                        ) : (
                          <CheckCircle2
                            className={`w-3.5 h-3.5 mt-0.5 shrink-0 ${
                              ok ? "text-green-500" : "text-zinc-400 dark:text-zinc-700"
                            }`}
                          />
                        )}
                        <span className="font-mono-tech text-zinc-700 dark:text-zinc-300 break-words">
                          {field.label}
                        </span>
                      </div>
                      <span className="font-mono-tech text-zinc-900 dark:text-white font-bold shrink-0">
                        {formatValue(field, val)}
                      </span>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}

          {media.length > 0 && (
            <div className="mb-4">
              <h4 className="font-display font-bold text-xs uppercase tracking-widest text-orange-500 border-b border-zinc-200 dark:border-zinc-800 pb-1 mb-2">
                Evidencias adjuntas ({media.length})
              </h4>
              <div className="grid grid-cols-3 gap-2">
                {media.map((m, idx) => (
                  <a
                    key={`${m.url}-${idx}`}
                    href={m.url}
                    target="_blank"
                    rel="noreferrer"
                    className="aspect-square bg-white dark:bg-zinc-950 border border-zinc-200 dark:border-zinc-800 rounded-sm overflow-hidden relative block"
                  >
                    {m.type === "video" ? (
                      <>
                        <video src={m.url} className="w-full h-full object-cover" muted />
                        <div className="absolute top-1 left-1 bg-black/70 px-1 py-0.5 rounded-sm flex items-center gap-0.5">
                          <Video className="w-2.5 h-2.5 text-orange-500" />
                          <span className="font-mono-tech text-[8px] text-white">VID</span>
                        </div>
                      </>
                    ) : (
                      <img src={m.url} alt={m.name || ""} className="w-full h-full object-cover" />
                    )}
                  </a>
                ))}
              </div>
            </div>
          )}

          <div className="mt-4 border-t border-dashed border-zinc-300 dark:border-zinc-700 pt-3">
            <p className="font-mono-tech text-[9px] uppercase tracking-widest text-zinc-500 mb-2">
              Firma del ingeniero
            </p>
            <div className="bg-white rounded-sm h-32 flex items-center justify-center overflow-hidden border border-zinc-200">
              {insp.signatureUrl ? (
                <img src={insp.signatureUrl} alt="Firma" className="max-h-full max-w-full object-contain" />
              ) : (
                <span className="font-mono-tech text-xs text-zinc-500">Sin firma</span>
              )}
            </div>
            <p className="font-mono-tech text-[10px] text-zinc-600 dark:text-zinc-400 mt-2 text-center">
              {insp.engineerName || insp.engineerUserUid} · {new Date(insp.createdAt).toLocaleDateString()}
            </p>
          </div>

          <div className="mt-4 stripes-warning h-2 rounded-sm" />
        </div>
      </div>

      <div className="border-t border-zinc-200 dark:border-zinc-800 bg-white/95 dark:bg-zinc-950/95 backdrop-blur-md p-4 shrink-0 flex gap-2">
        <button
          onClick={() => navigate("/", { replace: true })}
          data-testid="evidence-home-btn"
          className="flex-1 bg-zinc-100 dark:bg-zinc-800 hover:bg-zinc-200 dark:hover:bg-zinc-700 text-zinc-900 dark:text-white font-mono-tech text-xs uppercase tracking-widest h-12 rounded-sm border border-zinc-200 dark:border-zinc-700 transition-colors flex items-center justify-center gap-2"
        >
          <Home className="w-4 h-4" />
          Inicio
        </button>
        <button
          onClick={() => window.print()}
          data-testid="evidence-print-btn"
          className="flex-1 bg-orange-500 hover:bg-orange-600 text-white font-display font-bold uppercase tracking-widest h-12 rounded-sm transition-all glow-orange flex items-center justify-center gap-2"
        >
          <Printer className="w-4 h-4" />
          Imprimir / PDF
        </button>
      </div>
    </div>
  );
}
